// src/components/ProtectedFunnelRoute.tsx
// Guards /eedge/* routes that require a funnel token.
// Token can be either:
// 1) JWT issued by backend, or
// 2) Anonymous id fallback set during funnel flow.
// If missing, or JWT is expired → redirect to /eedge/ (or email gate for program-plan)

import { FormEvent, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import api, { getValidFunnelToken } from '../pages/ExecEdge-Funnel/api';

function readSavedFunnelEmail(): string {
  try {
    const progressRaw = localStorage.getItem('execedge_funnel_progress');
    if (progressRaw) {
      const progress = JSON.parse(progressRaw) as { formData?: { email?: string } };
      const email = progress?.formData?.email?.trim();
      if (email) return email;
    }
    const demoRaw = localStorage.getItem('execedge_demo_formdata');
    if (demoRaw) {
      const demo = JSON.parse(demoRaw) as { email?: string };
      const email = demo?.email?.trim();
      if (email) return email;
    }
  } catch {
    // Ignore broken localStorage payloads.
  }
  return '';
}

function ProgramPlanEmailGate({
  redirectTo,
  deepLinkBase,
}: {
  redirectTo: string;
  deepLinkBase: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState(readSavedFunnelEmail);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const qs = location.search || '';
  const redirectPath = qs ? `${redirectTo.split('?')[0]}${qs}` : redirectTo;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError('Please enter a valid email.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      const res = await api.get(`/lead/by-email/${encodeURIComponent(normalized)}`);
      const leadId = res.data?._id;
      if (leadId) {
        const target = `${deepLinkBase}/${leadId}${qs}`;
        navigate(target, { replace: true });
        return;
      }
      navigate(redirectPath, { replace: true });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        navigate(redirectPath, { replace: true });
        return;
      }
      setError('Could not look up your email right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 1000,
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid color-mix(in srgb, var(--warning) 35%, transparent)',
          borderRadius: '16px',
          maxWidth: '440px',
          width: '100%',
          padding: '22px',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <h3 style={{ margin: '0 0 10px', fontSize: '1.2rem', color: 'var(--text-primary)' }}>
          Enter your email to continue
        </h3>
        <p style={{ margin: '0 0 18px', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
          We&apos;ll restore your program plan if you&apos;ve already started the ExecEdge journey.
        </p>
        <label htmlFor="program-plan-email" style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Email
        </label>
        <input
          id="program-plan-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder="you@company.com"
          disabled={submitting}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            marginBottom: error ? '8px' : '16px',
            padding: '12px 14px',
            borderRadius: '10px',
            border: '1px solid var(--border-default)',
            background: 'var(--control-bg)',
            color: 'var(--text-primary)',
            fontSize: '1rem',
          }}
        />
        {error ? (
          <p style={{ margin: '0 0 12px', color: 'var(--text-danger)', fontSize: '0.875rem' }}>{error}</p>
        ) : null}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="ee-btn"
            onClick={() => navigate(redirectPath, { replace: true })}
            disabled={submitting}
          >
            Start from beginning
          </button>
          <button
            type="submit"
            className={`ee-btn ee-btn--gold ${submitting ? 'is-disabled' : ''}`}
            disabled={submitting}
          >
            {submitting ? 'Checking...' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  children: React.ReactNode;
  // Optional: override redirect destination (default: /eedge)
  redirectTo?: string;
  /** When true, prompt for email and deep-link to /eedge/program-plan/:leadId if found */
  emailGate?: boolean;
  /** Base path for lead deep link (default: /eedge/program-plan) */
  emailGateDeepLinkBase?: string;
}

export function ProtectedFunnelRoute({
  children,
  redirectTo = '/eedge',
  emailGate = false,
  emailGateDeepLinkBase = '/eedge/program-plan',
}: Props) {
  const location = useLocation();
  const [status, setStatus] = useState<'checking' | 'valid' | 'invalid'>('checking');

  useEffect(() => {
    const token = getValidFunnelToken();
    setStatus(token ? 'valid' : 'invalid');
  }, []);

  // Don't flash the protected content or redirect while checking
  if (status === 'checking') return null;

  if (status === 'invalid') {
    if (emailGate) {
      return (
        <ProgramPlanEmailGate
          redirectTo={redirectTo}
          deepLinkBase={emailGateDeepLinkBase}
        />
      );
    }

    // Keep query string (UTMs, etc.) so PageTracker + hv_utm still see params on /eedge
    const qs = location.search || '';
    const to = qs ? `${redirectTo.split('?')[0]}${qs}` : redirectTo;
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
}